import Chatbot from './Chatbot';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import {
  FaPhone, FaEnvelope, FaFacebook, FaLinkedin,
  FaTwitter, FaInstagram, FaYoutube, FaBars, FaTimes,
  FaChevronDown, FaMapMarkerAlt, FaArrowRight,
} from 'react-icons/fa';
import { adminAPI, subscribeAPI, productAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const BASE_NAV = [
  { label: 'Home',     to: '/' },
  { label: 'Products', to: '/products', dropdown: [] },
  { label: 'Careers',  to: '/careers' },
  { label: 'About',    to: '/about', dropdown: [
    { label: 'About Softlogic', to: '/about' },
    { label: 'Meet Our Team',   to: '/team'  },
    { label: 'Testimonials',    to: '/testimonials' },
  ]},
  { label: 'Contact',  to: '/contact' },
];

const PRODUCT_CATEGORIES = [
  {
    label: 'Food Management',
    icon: '🍽️',
    color: '#00d4ff',
    items: [
      { label: 'Chefmax Web Suite',  to: '/products/chefmax-web-suite',        desc: 'Patient meal ordering, dietary management & HL7 integration for hospitals & aged care.' },
      { label: 'Right Food Audit',   to: '/products/right-food-audit-system',   desc: 'Bluetooth temperature probes, HACCP checklists & automated compliance reporting.' },
    ],
  },
  {
    label: 'IoT Solutions',
    icon: '📡',
    color: '#10b981',
    items: [
      { label: 'Right IOT Platform', to: '/products/right-iot-solution',        desc: 'Real-time device monitoring, intelligent alerting & 600+ dashboard widgets. AU hosted.' },
    ],
  },
  {
    label: 'Business Software',
    icon: '💼',
    color: '#8b5cf6',
    items: [
      { label: 'NOVIQO ERP',         to: '/products/noviqo-erp-solution',       desc: 'All-in-one ERP with accounts, payroll, inventory & full Australian compliance built in.' },
    ],
  },
];

// ── Mobile nav item ───────────────────────────────────────────────────────────
function MobileNavItem({ label, to, dropdown, isActive, onClose }) {
  const [open, setOpen] = React.useState(false);
  if (!dropdown) {
    return (
      <Link to={to} onClick={onClose} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.85rem 0.5rem', color: isActive ? '#00d4ff' : '#ffffff',
        fontFamily: 'var(--font-display)', fontWeight: isActive ? 700 : 500,
        borderBottom: '1px solid rgba(255,255,255,0.07)', fontSize: '1rem', textDecoration: 'none',
      }}>{label}</Link>
    );
  }
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: open ? '1px solid rgba(0,212,255,0.2)' : '1px solid rgba(255,255,255,0.07)',
        background: open ? 'rgba(0,212,255,0.04)' : 'transparent',
      }}>
        <Link to={to} onClick={onClose} style={{
          flex: 1, padding: '0.85rem 0.5rem', color: isActive ? '#00d4ff' : '#ffffff',
          fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '1rem', textDecoration: 'none',
        }}>{label}</Link>
        <div onClick={() => setOpen(o => !o)} style={{ padding: '0.85rem 0.75rem', cursor: 'pointer', userSelect: 'none', borderLeft: '1px solid rgba(255,255,255,0.07)' }}>
          <span style={{ fontSize: '0.7rem', color: '#00d4ff', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
        </div>
      </div>
      {open && (
        <div style={{ background: 'rgba(0,212,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          {dropdown.map(item => (
            <Link key={item.to} to={item.to} onClick={onClose} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '0.8rem 0.5rem 0.8rem 1.5rem',
              color: '#ffffff', fontSize: '0.95rem', fontWeight: 500,
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              textDecoration: 'none',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00d4ff', flexShrink: 0 }} />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const toAbsoluteUrl = (url) => {
  if (!url || url.trim() === '') return null;
  if (url.includes('/uploads/')) return url.substring(url.indexOf('/uploads/'));
  return url;
};

// ── Mega Menu — categories left, products right on hover ─────────────────────
function MegaMenu({ onClose }) {
  const [hoveredCat, setHoveredCat] = React.useState(PRODUCT_CATEGORIES[0].label);
  const activeCat = PRODUCT_CATEGORIES.find(c => c.label === hoveredCat) || PRODUCT_CATEGORIES[0];

  return (
    <div style={{
      background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16,
      boxShadow: '0 12px 40px rgba(0,0,0,0.14)', display: 'flex',
      minWidth: 620, overflow: 'hidden',
    }}>
      {/* Left — categories */}
      <div style={{ width: 230, background: '#f8fafc', borderRight: '1px solid #e2e8f0', padding: '8px 0' }}>
        <div style={{ padding: '10px 16px 8px', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'linear-gradient(135deg,#0f172a,#0066ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Product Areas
        </div>
        {PRODUCT_CATEGORIES.map(cat => (
          <div key={cat.label}
            onMouseEnter={() => setHoveredCat(cat.label)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px', cursor: 'pointer', transition: 'all 0.15s',
              background: hoveredCat === cat.label ? '#e0f2fe' : 'transparent',
              borderLeft: hoveredCat === cat.label ? '3px solid #00d4ff' : '3px solid transparent',
            }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: hoveredCat === cat.label ? 'rgba(0,212,255,0.12)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0, transition: 'all 0.15s' }}>
              {cat.icon}
            </div>
            <span style={{ fontSize: '0.88rem', fontWeight: hoveredCat === cat.label ? 700 : 500, color: hoveredCat === cat.label ? '#0066ff' : '#334155', fontFamily: 'var(--font-display)', transition: 'color 0.15s' }}>
              {cat.label}
            </span>
            {hoveredCat === cat.label && (
              <span style={{ marginLeft: 'auto', color: '#00d4ff', fontSize: '0.75rem' }}>›</span>
            )}
          </div>
        ))}
        <div style={{ borderTop: '1px solid #e2e8f0', margin: '8px 0' }}/>
        <Link to="/products" onClick={onClose}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', color: '#0066ff', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}
          onMouseEnter={e => e.currentTarget.style.color = '#0043cc'}
          onMouseLeave={e => e.currentTarget.style.color = '#0066ff'}>
          View All Products →
        </Link>
      </div>

      {/* Right — products for hovered category */}
      <div style={{ flex: 1, padding: '16px 12px' }}>
        <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'linear-gradient(135deg,#0f172a,#0066ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: 12, padding: '0 4px' }}>
          {activeCat.icon} &nbsp;{activeCat.label}
        </div>
        {activeCat.items.map(item => (
          <Link key={item.to} to={item.to} onClick={onClose}
            style={{ display: 'flex', flexDirection: 'column', padding: '12px 14px', borderRadius: 10, textDecoration: 'none', transition: 'background 0.15s', marginBottom: 4 }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.06)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00d4ff', flexShrink: 0 }}/>
              <span style={{ color: '#060d1a', fontSize: '0.92rem', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{item.label}</span>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.78rem', lineHeight: 1.6, margin: 0, paddingLeft: 14 }}>{item.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Scroll to top on every route change ──────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

export default function PublicLayout() {
  const [scrolled, setScrolled]     = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [navLinks, setNavLinks]     = useState(BASE_NAV);
  const [openDrop, setOpenDrop]     = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const dropRef    = useRef(null);
  const [subEmail, setSubEmail]     = useState('');
  const [subLoading, setSubLoad]    = useState(false);
  const [settings, setSettings]     = useState({});
  const [logoSrc, setLogoSrc]       = useState(null);
  const [logoError, setLogoError]   = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [pageReady, setPageReady]   = useState(false);
  const location = useLocation();

  useEffect(() => {
    productAPI.getAll({ limit: 50, is_active: 1 })
      .then(r => {
        const prods = r.data.data || [];
        const dropdown = prods.map(p => ({ label: p.name, to: `/products/${p.slug}` }));
        setNavLinks(BASE_NAV.map(n => n.label === 'Products' ? { ...n, dropdown } : n));
      }).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpenDrop(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setMenuOpen(false); setOpenDrop(null); }, [location]);

  useEffect(() => {
    adminAPI.getSettings().then(r => {
      const s = r.data.data || {};
      setSettings(s);
      const url = toAbsoluteUrl(s.logo_url);
      setLogoSrc(url);
      setLogoError(false);
      if (!url) setPageReady(true);
    }).catch(() => { setPageReady(true); });
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail) return;
    setSubLoad(true);
    try {
      await subscribeAPI.subscribe({ email: subEmail });
      toast.success('Successfully subscribed!');
      setSubEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Subscription failed');
    }
    setSubLoad(false);
  };

  const isActive = (to) => to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const navbarStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
    background: 'var(--navy)', backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.25)' : 'none',
    transition: 'box-shadow 0.3s',
  };

  const siteName = settings.site_name || 'Softlogic Australia';
  const showLogo  = logoSrc && !logoError;

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh' }}>
      <ScrollToTop />

      {/* ── Main Navbar ── */}
      <nav style={navbarStyle} className="softlogic-nav">
        <Container>
          <div className="navbar-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0' }}>

            {/* Logo */}
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
              <style>{`
                .logo-3d { filter:brightness(0) invert(1); transition:opacity 0.2s ease; }
                
        /* ── Mobile (phones ≤576px) ── */
        @media(max-width:576px){
          .softlogic-nav { top:0 !important; }
          .logo-3d { margin-top:4px !important; }
          .softlogic-main { padding-top:58px !important; }
        }
        /* ── Large mobile / small tablet (577px–767px) ── */
        @media(min-width:577px) and (max-width:767px){
          .softlogic-nav { top:0 !important; }
          .logo-3d { margin-top:4px !important; }
          .softlogic-main { padding-top:62px !important; }
        }
        /* ── Tablet (768px–991px) ── */
        @media(min-width:768px) and (max-width:991px){
          .softlogic-main { padding-top:70px !important; }
        }
    
                .logo-3d:hover { opacity:0.85; }
              `}</style>
              {logoSrc && !logoError ? (
                <img src={logoSrc} alt={siteName} className="logo-3d nav-logo"
                  style={{ display: logoLoaded ? 'block' : 'none' }}
                  onLoad={() => { setLogoLoaded(true); setPageReady(true); }}
                  onError={() => { setLogoError(true); setPageReady(true); }} />
              ) : logoError ? (
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.3rem', color: '#fff' }}>{siteName}</span>
              ) : (
                <div style={{ width: 140, height: 52 }} />
              )}
            </Link>

            {/* Desktop Nav */}
            <div ref={dropRef} style={{ display: 'flex', alignItems: 'center', gap: 2 }} className="d-none d-xl-flex">
              {navLinks.map(({ label, to, dropdown }) => (
                <div key={to} style={{ position: 'relative' }}>
                  <Link to={dropdown ? '#' : to}
                    onClick={(e) => { if (dropdown) { e.preventDefault(); setOpenDrop(openDrop === to ? null : to); } }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      color: isActive(to) ? '#fff' : 'rgba(255,255,255,0.82)',
                      fontWeight: isActive(to) ? 700 : 500, fontSize: '1rem',
                      padding: '0.5rem 1rem', borderRadius: 8, transition: 'all 0.18s',
                      fontFamily: 'var(--font-display)',
                      background: isActive(to) ? 'linear-gradient(135deg,#0066ff,#00d4ff)' : 'transparent',
                      boxShadow: isActive(to) ? '0 2px 12px rgba(14,165,233,0.4)' : 'none',
                      textDecoration: 'none', cursor: 'pointer',
                    }}
                    onMouseEnter={e => { if (!isActive(to)) { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; } }}
                    onMouseLeave={e => { if (!isActive(to)) { e.currentTarget.style.color = 'rgba(255,255,255,0.82)'; e.currentTarget.style.background = 'transparent'; } }}>
                    {label}
                    {dropdown && <FaChevronDown style={{ fontSize: '0.6rem', opacity: 0.6, transition:'transform 0.2s', transform: openDrop === to ? 'rotate(180deg)' : 'none' }} />}
                  </Link>

                  {dropdown && openDrop === to && (
                    <div style={{ position: 'absolute', top: '100%', left: label === 'Products' ? '-80px' : 0, paddingTop: 8, zIndex: 200 }}>
                      {label === 'Products' ? (
                        <MegaMenu onClose={() => setOpenDrop(null)} />
                      ) : (
                        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 200, padding: '6px 0' }}>
                          {dropdown.map(item => (
                            <Link key={item.to} to={item.to}
                              onClick={() => setOpenDrop(null)}
                              style={{ display: 'block', padding: '10px 18px', color: '#060d1a', fontSize: '0.92rem', fontWeight: 500, transition: 'all 0.15s', textDecoration: 'none' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#f0f7ff'; e.currentTarget.style.color = '#0066ff'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#060d1a'; }}>
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="d-none d-xl-flex" style={{ gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <Link to="/inquiry" style={{ background: 'linear-gradient(135deg,#0066ff,#00d4ff)', color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 700, padding: '0.6rem 1.5rem', borderRadius: 'var(--r-sm)', fontSize: '0.92rem', textDecoration: 'none', boxShadow: '0 2px 12px rgba(0,212,255,0.25)' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}>
                Request a Demo
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setMenuOpen(m => !m)} className="d-xl-none hamburger-btn"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 8, padding: '0.65rem 0.9rem', cursor: 'pointer', fontSize: '1.2rem' }}>
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </Container>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={{ background: '#060d1a', borderTop: '2px solid rgba(0,212,255,0.3)', padding: '0.5rem 0 1.25rem' }}>
            <Container>
              {navLinks.map(({ label, to, dropdown }) => (
                <MobileNavItem key={to} label={label} to={to} dropdown={dropdown} isActive={isActive(to)} onClose={() => setMenuOpen(false)} />
              ))}
              <div style={{ display: 'flex', gap: 10, marginTop: '1rem', flexWrap: 'wrap', paddingTop: '0.75rem', borderTop: '1px solid rgba(0,212,255,0.15)' }}>
                <Link to="/inquiry" onClick={() => setMenuOpen(false)} style={{ background: 'linear-gradient(135deg,#0066ff,#00d4ff)', color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 700, padding: '0.6rem 1.4rem', borderRadius: 'var(--r-sm)', fontSize: '0.9rem', textDecoration: 'none' }}>
                  Request a Demo
                </Link>
              </div>
            </Container>
          </div>
        )}
      </nav>

      {/* ── Page Content ── */}
      <main className="softlogic-main" style={{ paddingTop: 80 }}>
        <Outlet />
      </main>

      {/* ── Footer ── */}
      <footer style={{ background: 'var(--navy)', color: 'rgba(255,255,255,0.6)', paddingTop: 56, paddingBottom: 24, position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent 0%,#0066ff 30%,#00d4ff 50%,#0066ff 70%,transparent 100%)' }} />
        <Container>
          <div className="row g-4 mb-4">
            {/* Brand */}
            <div className="col-lg-4">
              <div style={{ marginBottom: 16 }}>
                {showLogo ? (
                  <img src={logoSrc} alt={siteName}
                    style={{ height: 70, maxWidth: 220, objectFit: 'contain', filter: 'brightness(0) invert(1) drop-shadow(0 2px 8px rgba(255,255,255,0.3))' }}
                    onError={() => setLogoError(true)} />
                ) : (
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.3rem', color: '#fff' }}>{siteName}</span>
                )}
              </div>
              <p style={{ fontSize: '0.88rem', lineHeight: 1.8, color: 'rgba(255,255,255,0.55)', marginBottom: 20 }}>
                {settings.site_tagline || 'Where innovation meets efficiency.'} Your one-stop destination for cutting-edge software solutions.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  [settings.facebook_url  || 'https://www.facebook.com/AustraliaSoftlogic',                     FaFacebook],
                  [settings.linkedin_url  || 'https://www.linkedin.com/company/australia-softlogic/',            FaLinkedin],
                  [settings.twitter_url   || 'https://x.com/logic_soft14254',                                    FaTwitter],
                  [settings.instagram_url || 'https://www.instagram.com/softlogicaustralia/',                    FaInstagram],
                  [settings.youtube_url   || 'https://www.youtube.com/channel/UCu75VofUBA_kINZu2WtsQow',         FaYoutube],
                ].map(([href, Icon]) => (
                  <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                    style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(0,212,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.45)', transition: 'all 0.2s', textDecoration: 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--sky)'; e.currentTarget.style.color = 'var(--sky)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}>
                    <Icon style={{ fontSize: '0.85rem' }} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-6 col-lg-2">
              <h6 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Quick Links</h6>
              {[['/', 'Home'],['/products','Products'],['/careers','Careers'],['/about','About Us'],['/team','Our Team'],['/contact','Contact']].map(([h, l]) => (
                <Link key={l} to={h} style={{ display: 'block', color: 'rgba(255,255,255,0.55)', fontSize: '0.86rem', padding: '3px 0', transition: 'color 0.2s, padding-left 0.2s', textDecoration: 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#00d4ff'; e.currentTarget.style.paddingLeft = '8px'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; e.currentTarget.style.paddingLeft = '0'; }}>{l}</Link>
              ))}
            </div>

            {/* Solutions */}
            <div className="col-6 col-lg-2">
              <h6 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Solutions</h6>
              {[
                ['chefmax-web-suite',      'Chefmax'],
                ['right-iot-solution',     'Right IOT'],
                ['right-food-audit-system','Food Audit'],
                ['noviqo-erp-solution',    'NOVIQO ERP'],
              ].map(([slug, l]) => (
                <Link key={slug} to={`/products/${slug}`} style={{ display: 'block', color: 'rgba(255,255,255,0.55)', fontSize: '0.86rem', padding: '3px 0', transition: 'color 0.2s, padding-left 0.2s', textDecoration: 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#00d4ff'; e.currentTarget.style.paddingLeft = '8px'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; e.currentTarget.style.paddingLeft = '0'; }}>{l}</Link>
              ))}
            </div>

            {/* Contact + Newsletter */}
            <div className="col-lg-4">
              <h6 style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Contact Us</h6>
              {[
                [FaPhone,        settings.contact_phone   || '(03) 9543 7566',                                   `tel:${(settings.contact_phone||'0395437566').replace(/\s/g,'')}`],
                [FaEnvelope,     settings.contact_email   || 'sales@softlogic.com.au',                           `mailto:${settings.contact_email||'sales@softlogic.com.au'}`],
                [FaMapMarkerAlt, settings.contact_address || 'Unit 33, 15 Ricketts Rd, Mount Waverley VIC 3149', null],
              ].map(([Icon, text, href]) => (
                <div key={text} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
                  <Icon style={{ color: 'var(--sky)', marginTop: 3, flexShrink: 0, fontSize: '0.8rem' }} />
                  {href
                    ? <a href={href} style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.86rem', textDecoration: 'none' }}>{text}</a>
                    : <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.86rem', lineHeight: 1.5 }}>{text}</span>}
                </div>
              ))}
              <div style={{ marginTop: 18 }}>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', fontWeight: 600, marginBottom: 8 }}>Subscribe to Newsletter</p>
                <form onSubmit={handleSubscribe} style={{ display: 'flex', gap: 6 }}>
                  <input type="email" value={subEmail} onChange={e => setSubEmail(e.target.value)}
                    placeholder="your@email.com" required
                    style={{ flex: 1, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 7, padding: '0.45rem 0.75rem', fontSize: '0.82rem', outline: 'none' }} />
                  <button type="submit" disabled={subLoading}
                    style={{ background: 'linear-gradient(135deg, #0066ff, #00d4ff)', color: '#fff', border: 'none', borderRadius: 7, padding: '0.45rem 1rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0 }}>
                    {subLoading ? '…' : 'Subscribe'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '0 0 18px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', margin: 0 }}>
              © {new Date().getFullYear()} {settings.site_name || 'Softlogic Australia Pty Ltd'}. All rights reserved.
            </p>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', margin: 0 }}>Where Innovation Meets Efficiency</p>
          </div>
        </Container>
      </footer>
      <Chatbot />
    </div>
  );
}
